import { toastUncaptured } from 'components/NotificationBell/uncapturedToasts'
import { useEffect, useState } from 'react'

/**
 * The runs the user has sent to the background - servicing reports and statement batches - and the
 * single poller that watches them.
 *
 * Module-level rather than component state: the point of backgrounding a run is that the user
 * leaves the page that started it, so nothing tied to that page's lifetime can own it. Same
 * one-bus pattern as `notificationSync`.
 *
 * The server is the authority on a run's state; this only remembers which runs are the user's and
 * what to call them, so a reload or a second tab can pick them back up.
 */

/**
 * The API layer, pulled in on demand rather than imported at the top of this file.
 *
 * This module is reached from the base layout, which is evaluated while the app boots. A static
 * `import ... from 'services'` here moves `services` (and through it `config`) earlier in the
 * module graph, which lands inside the existing `config/index` to `config/company.constants`
 * import cycle and leaves `companyName` undefined: every page renders blank. Loading it lazily
 * keeps this file out of that part of the graph. Webpack resolves it once and caches it.
 */
const api = () => import('services')

/** File size for the tray line. A portfolio-wide report runs to hundreds of MB, so KB alone is unreadable. */
const formatBytes = (bytes?: number) => {
  if (!bytes) return undefined
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export type BackgroundRunKind = 'report' | 'statement' | 'notice' | 'hmdaPipe'
/** Which notice ladder a `notice` run belongs to, so the poller picks the right endpoints. */
export type NoticeKind = 'late' | 'insurance' | 'balloon' | 'rate'
export type BackgroundRunStatus = 'queued' | 'running' | 'done' | 'error' | 'canceled'

export interface BackgroundRun {
  kind: BackgroundRunKind
  jobId: string
  /** What to call it in the tray - the report type, or the statement period. */
  label: string
  status: BackgroundRunStatus
  startedAt: number
  /** Download name, reports only. */
  fileName?: string
  /** A progress or result line ("1,204 of 3,318", "Generated 812 statement(s)"). */
  detail?: string
  /** Only on `notice` runs. */
  noticeKind?: NoticeKind
  error?: string
}

type Listener = () => void

const StorageKey = 'nexys.backgroundRuns'
/** Matches the server's one-hour retention: past that a finished run's file is gone anyway. */
const RetentionMs = 60 * 60 * 1000
const PollIntervalMs = 2000

let runs: BackgroundRun[] = []
const listeners = new Set<Listener>()
let timer: any = null
/** Set once the persisted list has been read back, so a remount does not re-read over live state. */
let restored = false

const emit = () => {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch {
      // A listener from an unmounting tray must not break the others.
    }
  })
}

const persist = () => {
  try {
    localStorage.setItem(StorageKey, JSON.stringify(runs))
  } catch {
    // A full or disabled localStorage costs the reload-resume, not the feature.
  }
}

const setRuns = (next: BackgroundRun[]) => {
  runs = next
  persist()
  emit()
  syncTimer()
}

const patch = (jobId: string, changes: Partial<BackgroundRun>) => {
  setRuns(runs.map((run) => (run.jobId === jobId ? { ...run, ...changes } : run)))
}

export const getBackgroundRuns = () => runs

/** Subscribe to tray changes. Returns an unsubscribe usable directly as a `useEffect` cleanup. */
export const onBackgroundRunsChanged = (listener: Listener): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Announce a finished run. Uncaptured because the backend logs its own bell notification. */
const announce = (run: BackgroundRun, status: BackgroundRunStatus, detail?: string) => {
  if (status === 'done') {
    const downloadable = run.kind === 'report' || run.kind === 'hmdaPipe'
    toastUncaptured(downloadable ? `${run.label} is ready to download` : detail || `${run.label} finished`, {
      type: 'success',
    })
  } else if (status === 'error') {
    toastUncaptured(`${run.label} could not be generated`, { type: 'error' })
  }
}

/** Loans written so far, for the tray line. */
const hmdaProgress = (job: { handled?: number; total?: number }) => {
  const handled = Number(job.handled || 0)
  const total = Number(job.total || 0)
  if (total > 0) return `${handled.toLocaleString()} of ${total.toLocaleString()}`
  if (handled > 0) return `${handled.toLocaleString()} loans`
  return 'Starting…'
}

/** One poll of one run. Leaves the run alone on a transport error so a blip does not lose it. */
const poll = async (run: BackgroundRun) => {
  try {
    if (run.kind === 'hmdaPipe') {
      const res: any = await (await api()).getHmdaPipeJob(run.jobId)
      if (res?.success === 0) {
        patch(run.jobId, { status: 'error', error: res?.message || 'This run is no longer available.' })
        return
      }
      const job = res?.job
      if (!job) return
      if (job.status === 'queued') {
        patch(run.jobId, {
          status: 'queued',
          detail: job.queuedBehind ? `${job.queuedBehind} run(s) ahead` : 'Waiting to start',
        })
        return
      }
      if (job.status === 'running') {
        patch(run.jobId, { status: 'running', detail: hmdaProgress(job) })
        return
      }
      const detail = job.status === 'done' ? formatBytes(job.bytes) : undefined
      patch(run.jobId, { status: job.status, detail, error: job.error, fileName: job.fileName || run.fileName })
      announce(run, job.status, detail)
      return
    }

    if (run.kind === 'report') {
      const res: any = await (await api()).getServicingReportJob(run.jobId)
      if (res?.success === 0) {
        // The server forgot it (restart, or past its retention). Say so rather than polling forever.
        patch(run.jobId, { status: 'error', error: res?.message || 'This run is no longer available.' })
        return
      }
      const job = res?.job
      if (!job) return
      if (job.status === 'queued') {
        // Reports run one at a time on the server; say so rather than showing a stalled bar.
        patch(run.jobId, {
          status: 'queued',
          detail: job.queuedBehind ? `${job.queuedBehind} run(s) ahead` : 'Waiting to start',
        })
        return
      }
      if (job.status === 'running') {
        patch(run.jobId, { status: 'running', detail: undefined })
        return
      }
      const detail = job.status === 'done' ? formatBytes(job.bytes) : undefined
      patch(run.jobId, { status: job.status, detail, error: job.error, fileName: job.fileName || run.fileName })
      announce(run, job.status, detail)
      return
    }

    const svc = await api()
    const statusFor: Record<string, (id: string) => Promise<any>> = {
      late: svc.getLateNoticeStatus,
      insurance: svc.getInsuranceNoticeStatus,
      balloon: svc.getBalloonNoticeStatus,
      rate: svc.getRateNoticeStatus,
    }
    // Notices and statements report the same {total, handled, done, canceled} shape.
    const st: any =
      run.kind === 'notice'
        ? await statusFor[run.noticeKind || 'late'](run.jobId)
        : await svc.getStatementGenerationStatus(run.jobId)
    if (st?.success === 0) {
      patch(run.jobId, { status: 'error', error: st?.message || 'This run is no longer available.' })
      return
    }
    if (!st?.done) {
      // Portfolio generators share one lane, so a run can be waiting on an earlier one. Say that
      // rather than showing a progress bar that will not move for another twenty minutes.
      if (st?.queued) {
        patch(run.jobId, {
          status: 'queued',
          detail: st.queuedBehind ? `${st.queuedBehind} run(s) ahead` : 'Waiting to start',
        })
        return
      }
      const detail = st?.total
        ? `${Number(st.handled || 0).toLocaleString()} of ${Number(st.total).toLocaleString()}`
        : 'Starting…'
      patch(run.jobId, { detail, status: st?.canceled ? 'canceled' : 'running' })
      return
    }
    const message =
      st.error ||
      st.result?.message ||
      (run.kind === 'notice' && st.result ? `Generated ${(st.result.generated || []).length} notice(s)` : undefined)
    // A run that was called off reports what it managed to produce, not a failure.
    const status: BackgroundRunStatus = st.error ? 'error' : st.result?.canceled ? 'canceled' : 'done'
    patch(run.jobId, { status, detail: st.error ? undefined : message, error: st.error })
    announce(run, status, message)
  } catch {
    // Network hiccup: try again on the next tick.
  }
}

/** Queued counts as in flight: it has not finished, and it still needs polling. */
const isActive = (run: BackgroundRun) => run.status === 'running' || run.status === 'queued'

const tick = () => {
  runs.filter(isActive).forEach(poll)
}

/** The poller runs only while something is in flight. */
const syncTimer = () => {
  const active = runs.some(isActive)
  if (active && !timer) timer = setInterval(tick, PollIntervalMs)
  if (!active && timer) {
    clearInterval(timer)
    timer = null
  }
}

/**
 * Read back what this browser was watching, and reconcile it against the server.
 *
 * The server list is authoritative for reports: it corrects any run whose end this tab missed
 * while it was closed, and tells us which remembered runs the server has since dropped.
 *
 * Nothing stored means nothing to reconcile, and the call is skipped: this runs on every page load
 * for every signed-in user, most of whom have never run a servicing report. The cost of that is
 * that a run started in another tab is not picked up here - the bell notification covers it.
 */
export const restoreBackgroundRuns = async () => {
  if (restored) return
  restored = true
  try {
    const raw = localStorage.getItem(StorageKey)
    const saved: BackgroundRun[] = raw ? JSON.parse(raw) : []
    runs = (Array.isArray(saved) ? saved : []).filter((run) => Date.now() - (run.startedAt || 0) < RetentionMs)
  } catch {
    runs = []
  }
  if (!runs.length) {
    setRuns([])
    return
  }

  try {
    const svc = await api()
    const [reportRes, hmdaRes] = await Promise.all([
      svc.getServicingReportJobs().catch(() => null),
      svc.getHmdaPipeJobs().catch(() => null),
    ])
    const jobs: any[] = reportRes?.jobs || []
    const hmdaJobs: any[] = hmdaRes?.jobs || []
    const byId = new Map(runs.map((run) => [run.jobId, run]))
    if (reportRes) {
      jobs.forEach((job) => {
        const existing = byId.get(job.jobId)
        // Only runs this browser already knew about: a finished report from an hour ago, or one
        // started in another tab, is not something to reopen this tray for.
        if (!existing) return
        byId.set(job.jobId, {
          kind: 'report',
          jobId: job.jobId,
          label: existing?.label || job.type,
          status: job.status,
          startedAt: job.startedAt,
          fileName: job.fileName,
          detail: job.status === 'done' ? formatBytes(job.bytes) : undefined,
          error: job.error,
        })
      })
    }
    if (hmdaRes) {
      hmdaJobs.forEach((job) => {
        const existing = byId.get(job.jobId)
        if (!existing) return
        byId.set(job.jobId, {
          kind: 'hmdaPipe',
          jobId: job.jobId,
          label: existing?.label || 'HMDA pipe file',
          status: job.status,
          startedAt: job.startedAt,
          fileName: job.fileName,
          detail:
            job.status === 'done'
              ? formatBytes(job.bytes)
              : job.status === 'running'
                ? hmdaProgress(job)
                : existing.detail,
          error: job.error,
        })
      })
    }
    // A run this browser remembers that the server has forgotten is gone for good.
    runs = [...byId.values()].filter((run) => {
      if (run.kind === 'report' && reportRes) return jobs.some((job) => job.jobId === run.jobId)
      if (run.kind === 'hmdaPipe' && hmdaRes) return hmdaJobs.some((job) => job.jobId === run.jobId)
      return true
    })
  } catch {
    // Offline or unauthorised: fall back to what was stored.
  }
  setRuns([...runs].sort((a, b) => b.startedAt - a.startedAt))
}

/** Put a started servicing-report run in the tray. */
export const trackReportRun = (jobId: string, label: string, fileName?: string) => {
  setRuns([
    { kind: 'report', jobId, label, status: 'queued', detail: 'Waiting to start', startedAt: Date.now(), fileName },
    ...runs,
  ])
}

/** Put a started HMDA pipe run in the tray. */
export const trackHmdaPipeRun = (jobId: string, label: string, fileName?: string) => {
  setRuns([
    {
      kind: 'hmdaPipe',
      jobId,
      label,
      status: 'queued',
      detail: 'Waiting to start',
      startedAt: Date.now(),
      fileName,
    },
    ...runs,
  ])
}

/** Put a started statement run in the tray. */
export const trackStatementRun = (jobId: string, label: string) => {
  setRuns([
    { kind: 'statement', jobId, label, status: 'queued', startedAt: Date.now(), detail: 'Waiting to start' },
    ...runs,
  ])
}

/** Put a started notice run in the tray. */
export const trackNoticeRun = (jobId: string, label: string, noticeKind: NoticeKind) => {
  setRuns([
    { kind: 'notice', noticeKind, jobId, label, status: 'queued', startedAt: Date.now(), detail: 'Waiting to start' },
    ...runs,
  ])
}

export const cancelBackgroundRun = async (jobId: string) => {
  const run = runs.find((item) => item.jobId === jobId)
  if (!run) return
  // Shown as canceled straight away; the worker stops at its next checkpoint.
  patch(jobId, { status: 'canceled', detail: run.kind === 'statement' && run.status === 'running' ? 'Stopping…' : undefined })
  try {
    const svc = await api()
    const cancelFor: Record<string, (id: string) => Promise<any>> = {
      late: svc.cancelLateNoticeRun,
      insurance: svc.cancelInsuranceNoticeRun,
      balloon: svc.cancelBalloonNoticeRun,
      rate: svc.cancelRateNoticeRun,
    }
    if (run.kind === 'report') await svc.cancelServicingReportJob(jobId)
    else if (run.kind === 'hmdaPipe') await svc.cancelHmdaPipeJob(jobId)
    else if (run.kind === 'notice') await cancelFor[run.noticeKind || 'late'](jobId)
    else await svc.cancelStatementGeneration(jobId)
  } catch {
    // The run is already marked canceled here; a failed call just means it may still finish.
  }
}

export const dismissBackgroundRun = async (jobId: string) => {
  const run = runs.find((item) => item.jobId === jobId)
  setRuns(runs.filter((item) => item.jobId !== jobId))
  const svc = await api()
  if (run?.kind === 'report') await svc.dismissServicingReportJob(jobId).catch(() => null)
  else if (run?.kind === 'hmdaPipe') await svc.dismissHmdaPipeJob(jobId).catch(() => null)
}

export const downloadBackgroundRun = async (jobId: string) => {
  const run = runs.find((item) => item.jobId === jobId)
  if (!run) return
  const svc = await api()
  if (run.kind === 'hmdaPipe') {
    await svc.downloadHmdaPipeJobFile(jobId, run.fileName || 'HMDA Report.txt')
    return
  }
  if (run.kind !== 'report') return
  await svc.downloadServicingReportJobFile(jobId, run.fileName || `${run.label}.pdf`)
}

/** Subscribe a component to the tray's contents. */
export const useBackgroundRuns = () => {
  const [items, setItems] = useState<BackgroundRun[]>(getBackgroundRuns)

  useEffect(() => {
    setItems(getBackgroundRuns())
    const off = onBackgroundRunsChanged(() => setItems(getBackgroundRuns()))
    restoreBackgroundRuns().then(() => setItems(getBackgroundRuns()))
    return () => {
      off()
    }
  }, [])

  return items
}
